import { animated, useSpring } from '@react-spring/web';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Impact } from '@sorare/core/src/atoms/animations/Impact';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconInfoCircle } from '@sorare/core/src/atoms/icons/system/iconInfoCircle.ts';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineS, LabelM } from '@sorare/core/src/atoms/typography';
import { range } from '@sorare/core/src/lib/arrays';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

const Root = styled(Horizontal).attrs({ center: true })`
  grid-area: golden;
  align-items: baseline;
  max-height: calc(var(--unit) * 6);
  width: 100%;
  @media ${tabletAndAbove} {
    flex-direction: column;
    max-height: none;
    align-items: center;
  }
`;

const getClipPath = (max: number, bevel = 5, gap = 8) =>
  range(max)
    .map(i => {
      const width = `calc((100% - (${max} - 1) * ${gap}px) / ${max})`;
      const offset = `calc(${i} * (${width} + ${gap}px))`;
      return [
        `${offset} 100%`,
        `${offset} ${bevel}px`,
        `calc(${offset} + ${bevel}px) 0`,
        `calc(${offset} + ${width}) 0`,
        `calc(${offset} + ${width}) calc(100% - ${bevel}px)`,
        `calc(${offset} + ${width} - ${bevel}px) 100%`,
      ].join(',');
    })
    .join(', ');

const GoldenWheelProgress = styled(animated.progress)<{ max: number }>`
  appearance: none;
  border: none;
  width: 100%;
  height: 14px;
  border-radius: 0px;
  color: var(--c-yellow-700);

  /* Create a shape to show the progress as if it was N bevel shaped segments */
  clip-path: polygon(${({ max }) => getClipPath(max, 0, 2)});
  @media ${tabletAndAbove} {
    clip-path: polygon(${({ max }) => getClipPath(max)});
  }

  &[value] {
    &::-moz-progress-bar {
      background: linear-gradient(
        0deg,
        var(--c-yellow-700) 0%,
        var(--c-yellow-200) 100%
      );
    }
    &::-webkit-progress-value {
      background: linear-gradient(
        0deg,
        var(--c-yellow-700) 0%,
        var(--c-yellow-200) 100%
      );
    }
    &::-webkit-progress-bar {
      background: linear-gradient(
        00deg,
        var(--c-nd-100) 0%,
        var(--c-nd-100) 100%
      );
    }
  }
`;

const Title = styled(LabelM)`
  white-space: nowrap;
  display: flex;
  align-items: center;

  @media ${tabletAndAbove} {
    white-space: normal;
  }
`;
const InfoCircle = styled.button.attrs({
  type: 'button',
})`
  margin-left: var(--half-unit);
`;
const GoldenHeadlineS = styled(HeadlineS)`
  &.golden {
    background: linear-gradient(
      194deg,
      #fff 1.37%,
      #fcff63 36.66%,
      #c26900 101.57%
    );
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

type Props = { nb: number; cost: number; onInfoCircle: () => void };

export const GoldenTickets = ({ nb, cost, onInfoCircle }: Props) => {
  const style = useSpring({
    number: nb,
    delay: 500,
  });
  const remainingSpins = Math.max(0, cost - nb);
  const isGoldenWheel = remainingSpins === 0;
  return (
    <Root>
      <Title color="var(--c-nd-600)">
        <FormattedMessage defaultMessage="Golden Spin Loader" />
        <InfoCircle onClick={onInfoCircle}>
          <FontAwesomeIcon icon={iconInfoCircle} size="sm" />
        </InfoCircle>
      </Title>
      <Impact trigger={remainingSpins} delay={500}>
        <GoldenHeadlineS className={isGoldenWheel ? 'golden' : ''}>
          {isGoldenWheel ? (
            <FormattedMessage defaultMessage="Next spin" />
          ) : (
            <FormattedMessage
              defaultMessage="{nb}/{cost} spins"
              values={{ cost, nb: Math.min(nb, cost) }}
            />
          )}
        </GoldenHeadlineS>
      </Impact>
      <GoldenWheelProgress value={style.number.to(n => n)} max={cost} />
    </Root>
  );
};
